import React, { useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { Outlet } from 'react-router-dom'
import EmptyState from 'components/EmptyState'
import Loader from 'components/Loader'

const withRouteEmptyState = (config) => {
  const RouteEmptyStateWrapper = () => {
    const location = useLocation()
    const { conditions = [] } = config

    const hookResults = conditions.map((condition) => ({
      id: condition.id,
      result: condition.useDataHook(),
      heading: condition.useHeadingHook
        ? condition.useHeadingHook()
        : condition.emptyStateProps.heading,
      description: condition.useDescriptionHook
        ? condition.useDescriptionHook()
        : condition.emptyStateProps.description,
      condition,
    }))

    const routeMatchingConditions = useMemo(() => {
      return hookResults.filter(({ condition }) => {
        return condition.routes.some((routePath) => {
          if (routePath === location.pathname) {
            return true
          }
          if (routePath.includes(':')) {
            const routePathParts = routePath.split('/')
            const currentPathParts = location.pathname.split('/')

            if (routePathParts.length !== currentPathParts.length) {
              return false
            }

            return routePathParts.every((part, index) => {
              return part.startsWith(':') || part === currentPathParts[index]
            })
          }
          return false
        })
      })
    }, [hookResults, location.pathname])

    if (routeMatchingConditions.length === 0) {
      return <Outlet />
    }

    for (const { result, heading, description, condition } of routeMatchingConditions) {
      const { data, isLoading, ...otherHookData } = result

      if (isLoading) {
        return <Loader />
      }

      const shouldShowEmptyState = condition.check(data, isLoading, ...Object.values(otherHookData))

      if (shouldShowEmptyState) {
        if (condition.fallbackComponent) {
          return <condition.fallbackComponent />
        }
        return (
          <EmptyState {...condition.emptyStateProps} heading={heading} description={description} />
        )
      }

    }

    return <Outlet />
  }

  RouteEmptyStateWrapper.displayName = 'withRouteEmptyState'

  return RouteEmptyStateWrapper
}

export default withRouteEmptyState
